import React, { useState } from 'react';
import './MobileDashboard.css';
import DashboardSettingsModal from './DashboardSettingsModal';
import AccountsManagerModal from './AccountsManagerModal';

const ACCOUNT_TYPE_META = {
  debit:          { icon: '💳', label: 'Debit Card' },
  credit:         { icon: '💳', label: 'Credit Card' },
  bank:           { icon: '🏦', label: 'Bank Account' },
  cash:           { icon: '💵', label: 'Cash' },
  upi:            { icon: '📱', label: 'UPI' },
  prepaid_wallet: { icon: '👛', label: 'Wallet' },
  metro_card:     { icon: '🚇', label: 'Metro Card' },
  food_card:      { icon: '🍱', label: 'Food Card' },
  other:          { icon: '📦', label: 'Other' },
};

const formatAmount = (num) => {
  const abs = Math.abs(num);
  return abs.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const QUICK_ACTIONS = [
  { id: 'add_transaction', icon: '➕', label: 'Add Transaction', sub: 'Record debit or credit' },
  { id: 'pay_cc_bill',     icon: '💳', label: 'Pay CC Bill',      sub: 'Clear card outstanding' },
  { id: 'bank_transfer',   icon: '🔄', label: 'Transfers',        sub: 'Move money between accounts' },
  { id: 'scheduled',       icon: '🗓️', label: 'Scheduled',        sub: 'Recurring payments' },
  { id: 'lend_borrow',     icon: '🤝', label: 'Lend & Borrow',    sub: 'Track loans and debts' },
  { id: 'investments',     icon: '📈', label: 'Investments',      sub: 'Stock markets & policies' },
];

export default function MobileDashboard({
  sym = '₹',
  balance = 0,
  owed = 0,
  spent = 0,
  income = 0,
  accounts = [],
  consolidatedAccounts = [],
  user,
  dashboards = [],
  selectedDashboardId,
  onDashboardChange,
  onAccountClick,
  onModeSelect,
  onDashboardsChanged,
}) {
  const displayName = user?.name || user?.email?.split('@')[0] || 'there';

  const [showSettings, setShowSettings] = useState(false);
  const [showAccountsMgr, setShowAccountsMgr] = useState(false);
  const realAccounts = accounts.filter(a => !a._virtual);
  const selectedDash = dashboards.find(d => d.id === selectedDashboardId);

  const groupedInstruments = React.useMemo(() => {
    if (!accounts.length) return [];
    const accountMap = {};
    realAccounts.forEach(a => { accountMap[a.id] = a; });
    const usedAccountIds = new Set();
    const result = [];
    (consolidatedAccounts || []).forEach(ca => {
      const members = (ca.account_ids || []).map(id => accountMap[id]).filter(Boolean);
      if (members.length > 0) {
        members.forEach(m => usedAccountIds.add(m.id));
        const total = members.reduce((s, a) => {
          const b = parseFloat(a.current_balance || 0);
          return s + (a.mechanism === 'pay_later' ? -b : b);
        }, 0);
        result.push({ ...ca, isGroup: true, members, total });
      }
    });
    realAccounts.forEach(a => {
      if (!usedAccountIds.has(a.id)) result.push(a);
    });
    return result;
  }, [accounts, consolidatedAccounts, realAccounts]);

  return (
    <div className="mdb-root">

      <div className="mdb-shell">

        {/* ── Hero strip ── */}
        <div className="mdb-hero">
          <div className="mdb-hero-top">
            <div className="mdb-hero-greet-block">
              <div className="mdb-hero-name">Hi, {displayName}</div>
              <div className="mdb-hero-sub">{new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
            </div>
            <div className="mdb-dash-controls">
              {selectedDash && (
                <button
                  type="button"
                  className="mdb-dash-settings"
                  onClick={() => setShowSettings(true)}
                  aria-label="Dashboard settings"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                </button>
              )}
              {dashboards.length > 1 && (
                <select
                  className="mdb-dash-select"
                  value={selectedDashboardId}
                  onChange={e => onDashboardChange && onDashboardChange(e.target.value)}
                >
                  {dashboards.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="mdb-hero-balance-block">
            <div className="mdb-hero-balance-lbl">Spending</div>
            <div className="mdb-hero-balance mdb-red">
              {sym}{formatAmount(spent)}
            </div>
          </div>

          <div className="mdb-hero-stats">
            <div className="mdb-hero-stat">
              <div className="mdb-hero-stat-lbl">Balance</div>
              <div className={`mdb-hero-stat-val ${balance < 0 ? 'mdb-red' : 'mdb-green'}`}>{sym}{formatAmount(balance)}</div>
            </div>
            <div className="mdb-hero-divider" />
            <div className="mdb-hero-stat">
              <div className="mdb-hero-stat-lbl">Owed</div>
              <div className="mdb-hero-stat-val mdb-yellow">{sym}{formatAmount(owed)}</div>
            </div>
            <div className="mdb-hero-divider" />
            <div className="mdb-hero-stat">
              <div className="mdb-hero-stat-lbl">Income</div>
              <div className="mdb-hero-stat-val mdb-green">{sym}{formatAmount(income)}</div>
            </div>
          </div>
        </div>

        {/* ── Quick actions ── */}
        <section className="mdb-pane mdb-pane--quick">
          <div className="mdb-pane-hd">
            <span className="mdb-pane-title">Quick Actions</span>
            <span className="mdb-pane-sub">Tap to begin</span>
          </div>
          <div className="mdb-actions-grid">
            {QUICK_ACTIONS.map(action => (
              <div
                key={action.id}
                role="button"
                tabIndex={0}
                className="mdb-action-card"
                onClick={() => onModeSelect && onModeSelect(action.id)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && onModeSelect) onModeSelect(action.id);
                }}
              >
                <div className="mdb-action-icon">{action.icon}</div>
                <div className="mdb-action-label">{action.label}</div>
                <div className="mdb-action-sub">{action.sub}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Instruments Vertical Section (Replaces Horizontal Accounts) ── */}
        <section className="mdb-pane mdb-pane--instruments">
          <div className="mdb-pane-hd">
            <span className="mdb-pane-title">Instruments</span>
            <div className="mdb-pane-hd-right">
              <span className="mdb-pane-sub">{realAccounts.length} total</span>
              <button
                type="button"
                className="mdb-section-action"
                onClick={() => setShowAccountsMgr(true)}
                aria-label="Manage instruments"
                title="Manage instruments"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="4" y1="6" x2="20" y2="6" />
                  <line x1="4" y1="12" x2="14" y2="12" />
                  <line x1="4" y1="18" x2="18" y2="18" />
                  <circle cx="18" cy="6" r="2" />
                  <circle cx="16" cy="12" r="2" />
                  <circle cx="20" cy="18" r="2" />
                </svg>
                <span>Manage</span>
              </button>
            </div>
          </div>
          
          <div className="mdb-inst-list">
            {groupedInstruments.length > 0 ? (
              groupedInstruments.map(item => {
                if (item.isGroup) {
                  return (
                    <div key={item.id} className="mdb-inst-group">
                      <div 
                        className="mdb-inst-group-hd"
                        onClick={() => onAccountClick && onAccountClick(item.members.map(m => m.id))}
                      >
                        <span className="mdb-inst-ico">📁</span>
                        <div className="mdb-inst-main">
                          <span className="mdb-inst-nm">{item.name}</span>
                        </div>
                        <span className={`mdb-inst-bal ${item.total >= 0 ? 'mdb-green' : 'mdb-red'}`}>
                          {sym}{formatAmount(item.total)}
                        </span>
                      </div>
                      <div className="mdb-inst-group-members">
                        {item.members.map(acc => {
                          const meta = ACCOUNT_TYPE_META[acc.account_type] || ACCOUNT_TYPE_META.other;
                          const bal = parseFloat(acc.current_balance || 0);
                          const isOwed = acc.mechanism === 'pay_later';
                          return (
                            <div
                              key={acc.id}
                              className="mdb-inst-row mdb-inst-row--indented"
                              onClick={() => onAccountClick && onAccountClick(acc.id)}
                            >
                              <span className="mdb-inst-ico">{meta.icon}</span>
                              <div className="mdb-inst-main">
                                <span className="mdb-inst-nm">{acc.name}</span>
                                {isOwed && <span className="mdb-inst-owed-tag">Owed</span>}
                              </div>
                              <span className={`mdb-inst-bal ${
                                isOwed
                                  ? (bal > 0 ? 'mdb-red' : 'mdb-green')
                                  : (bal >= 0 ? 'mdb-green' : 'mdb-red')
                              }`}>
                                {sym}{formatAmount(bal)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                const acc = item;
                const meta = ACCOUNT_TYPE_META[acc.account_type] || ACCOUNT_TYPE_META.other;
                const bal = parseFloat(acc.current_balance || 0);
                const isOwed = acc.mechanism === 'pay_later';
                return (
                  <div
                    key={acc.id}
                    className="mdb-inst-row"
                    onClick={() => onAccountClick && onAccountClick(acc.id)}
                  >
                    <span className="mdb-inst-ico">{meta.icon}</span>
                    <div className="mdb-inst-main">
                      <span className="mdb-inst-nm">{acc.name}</span>
                      {isOwed && <span className="mdb-inst-owed-tag">Owed</span>}
                    </div>
                    <span className={`mdb-inst-bal ${
                      isOwed
                        ? (bal > 0 ? 'mdb-red' : 'mdb-green')
                        : (bal >= 0 ? 'mdb-green' : 'mdb-red')
                    }`}>
                      {sym}{formatAmount(bal)}
                    </span>
                  </div>
                );
              })
            ) : (
              <div className="mdb-accounts-empty">
                No instruments yet — tap Manage to add your first one.
              </div>
            )}
          </div>
        </section>

      </div>

      {showSettings && selectedDash && (
        <DashboardSettingsModal
          dashboard={selectedDash}
          dashboards={dashboards}
          onClose={() => setShowSettings(false)}
          onChanged={() => onDashboardsChanged && onDashboardsChanged()}
          onSelectDashboard={(id) => onDashboardChange && onDashboardChange(id)}
        />
      )}

      <AccountsManagerModal
        open={showAccountsMgr}
        onClose={() => setShowAccountsMgr(false)}
        accounts={accounts}
        dashboardId={selectedDashboardId}
        onAddNew={() => {
          // Hand the user to the existing manage_instruments flow which
          // owns the full create form (the modal only handles reorder + hide).
          setShowAccountsMgr(false);
          if (onModeSelect) onModeSelect('manage_instruments');
        }}
      />
    </div>
  );
}
